import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { MapPin, ArrowRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import { usePageData } from "@/lib/pageData";
import { SITE_URL } from "@/lib/business";
import { cityAnchor } from "@/lib/serviceAreas";
import { api } from "@/lib/api";

const CITIES = [
  ["Bhopal", "the state capital and a growing hub for services, trading and new company registrations."],
  ["Indore", "Madhya Pradesh's commercial centre, with strong demand for GST, FSSAI and Shop & Establishment work."],
  ["Jabalpur", "a key city for manufacturing, trading and industrial licensing requirements."],
  ["Gwalior", "home to expanding retail, hospitality and healthcare businesses needing licensing support."],
  ["Satna", "known for cement and industrial activity, where factory, labour and PESO-related licences matter."],
  ["Rewa", "a growing centre for new businesses, MSMEs and transport operators."],
  ["Ujjain", "a major tourism and hospitality market with hotel, food and excise licensing needs."],
];

const INTENT_LINKS = [
  ["/services/gst-registration", "GST registration services in Madhya Pradesh"],
  ["/services/udyam-msme-registration", "MSME registration assistance in Madhya Pradesh"],
  ["/services/company-registration", "Company registration in Madhya Pradesh"],
  ["/services/shop-establishment-registration", "Shop & Establishment registration in Madhya Pradesh"],
  ["/services/fssai-licensing", "FSSAI licence assistance in Madhya Pradesh"],
  ["/services/drug-licence", "Drug licence assistance in Madhya Pradesh"],
  ["/services/category/business-labour-industrial-licensing", "Business licensing services in Madhya Pradesh"],
  ["/services/custom-website-development", "Website development company in Madhya Pradesh"],
  ["/services/mobile-app-development", "Mobile app development services in Madhya Pradesh"],
];

export default function MadhyaPradesh() {
  const initial = usePageData();
  const [categories, setCategories] = useState(initial?.categories || []);
  useEffect(() => {
    let active = true;
    api.get("/categories").then((response) => { if (active) setCategories(response.data); }).catch(() => {});
    return () => { active = false; };
  }, []);
  const jsonLd = [{
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
      { "@type": "ListItem", position: 2, name: "Madhya Pradesh", item: SITE_URL + "/madhya-pradesh" },
    ],
  }, {
    "@context": "https://schema.org", "@type": "ItemList",
    "@id": SITE_URL + "/madhya-pradesh#categories", name: "Service categories in Madhya Pradesh",
    numberOfItems: categories.length,
    itemListElement: categories.map((category, index) => ({
      "@type": "ListItem", position: index + 1,
      item: {
        "@type": "CollectionPage", "@id": SITE_URL + `/services/category/${category.slug}#webpage`,
        name: category.name, url: SITE_URL + `/services/category/${category.slug}`,
      },
    })),
  }];
  return (
    <div data-testid="mp-page">
      <SEO title="Business Services in Madhya Pradesh | Mynavodayans"
           description="Registration, licensing, compliance and digital project assistance from Satna for businesses across Madhya Pradesh and India. Confirm service availability."
           canonicalPath="/madhya-pradesh" pageType="CollectionPage" jsonLd={jsonLd} />
      <section className="bg-[#0a192f] py-16 text-white">
        <div className="container-x">
          <p className="eyebrow-label !text-[#f5c542]">Regional service area</p>
          <h1 className="mt-3 max-w-3xl font-heading text-3xl font-extrabold sm:text-4xl lg:text-5xl" data-testid="mp-headline">
            Supporting Businesses Across Madhya Pradesh
          </h1>
          <p className="mt-4 max-w-2xl text-white/75 md:text-lg">
            Our office is in Satna, Madhya Pradesh, and we serve businesses across India. This regional guide covers enquiries from Madhya Pradesh for registrations, licences, compliance and digital projects, with requirements confirmed for the service and location.
          </p>
          <Link to="/service-areas" className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#f5c542] hover:underline" data-testid="mp-india-coverage-link">Service Areas — India <ArrowRight size={15} /></Link>
        </div>
      </section>

      <section className="container-x py-16">
        <h2 className="font-heading text-2xl font-extrabold text-[#0a192f]">Where We Work in MP</h2>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600">
          We coordinate enquiries across the state over call and WhatsApp from our Satna office. Any local visit, verification or inspection depends on the service and responsible authority. These are existing regional service areas:
        </p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="mp-cities-grid">
          {CITIES.map(([city, text]) => (
            <div key={city} id={cityAnchor(city)} className="scroll-mt-28 rounded-2xl border border-slate-200 bg-white p-6">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-[#0a192f]">
                <MapPin size={16} className="text-[#d4a017]" /> {city}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">We assist businesses in {city}, {text}</p>
            </div>
          ))}
          <div className="rounded-2xl bg-[#0a192f] p-6 text-white">
            <h3 className="font-heading text-base font-bold text-[#f5c542]">Enquiries from Across India</h3>
            <p className="mt-2 text-sm leading-relaxed text-white/70">
              Our service coverage extends across India. Share your city, state and business requirement so we can confirm availability, the applicable process, any local steps and charges.
            </p>
            <Link to="/contact" className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#f5c542] hover:underline" data-testid="mp-enquiry-link">
              Request a Quote <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <section className="container-x pb-16" aria-labelledby="location-requirements">
        <h2 id="location-requirements" className="font-heading text-2xl font-extrabold text-[#0a192f]">How Your Location Affects the Work</h2>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">
          Our office is in Satna; the cities above are service areas. For registration and licensing enquiries, share the business activity, premises city and state, and whether the requirement is new, a renewal or a change. The applicable authority, records and any physical visit or inspection are confirmed for the specific case.
        </p>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">
          For accounting or professional coordination, include the period and records available. For websites and apps, tell us your audience, features and delivery needs. We confirm availability, responsibilities, fees and next steps before accepting work; government approvals remain with the relevant authority.
        </p>
      </section>

      <section id="service-categories" className="container-x scroll-mt-28 pb-16" aria-labelledby="location-categories-heading">
        <h2 id="location-categories-heading" className="font-heading text-2xl font-extrabold text-[#0a192f]">Explore All Service Categories</h2>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600">Choose a category to review its services, preparation checklist and enquiry options. Include your location when discussing availability.</p>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="mp-category-links">
          {categories.map((category) => (
            <Link key={category.slug} to={`/services/category/${category.slug}`} className="rounded-xl border border-slate-200 bg-white p-5 transition-colors hover:border-[#d4a017]">
              <span className="font-heading text-sm font-bold text-[#0a192f]">{category.name}</span>
              <span className="mt-2 block text-xs leading-relaxed text-slate-600">{category.short}</span>
            </Link>
          ))}
        </div>
        <Link to="/services" className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#b07f0a] underline underline-offset-4">Browse the complete service directory <ArrowRight size={14} /></Link>
      </section>

      <section className="bg-white py-16">
        <div className="container-x">
          <h2 className="font-heading text-2xl font-extrabold text-[#0a192f]">Popular Services in Madhya Pradesh</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="mp-intent-links">
            {INTENT_LINKS.map(([to, label]) => (
              <Link key={to} to={to}
                    className="flex items-center justify-between rounded-xl border border-slate-200 px-5 py-4 text-sm font-semibold text-slate-800 transition-colors hover:border-[#d4a017] hover:bg-amber-50"
                    data-testid={`mp-link-${label.slice(0, 20).replace(/\W+/g, "-").toLowerCase()}`}>
                {label} <ArrowRight size={15} className="text-[#d4a017]" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
